import * as angular from "angular";
import { Limit } from "../models/limit.model";
import { Organisation } from "../models/organisation.model";
import { CampaignService } from "../services/campaign.service";
import { OrganisationService } from "../services/organisation.service";
import { PermissionCheck } from "../shared/permission";
import { CampaignDataProvider } from "./campaign.dp";

export class LimitDataProvider {
    private $q: any;
    private service: CampaignService | OrganisationService;
    private organisation: Organisation;
    private readonly isLeaderView: boolean;
    private permissionProvider: PermissionCheck;
    private campaignDataProvider: CampaignDataProvider;
    private limitsPromise: any;
    public limits: Limit;
    private id: string;

    constructor(
        $q: any,
        CampaignService: CampaignService,
        OrganisationService: OrganisationService,
        CampaignDataProvider: CampaignDataProvider,
        PermissionCheck: PermissionCheck,
        __app_type: string
    ) {
        this.$q = $q;
        this.isLeaderView = __app_type == "LEADER";

        this.campaignDataProvider = CampaignDataProvider;
        this.permissionProvider = PermissionCheck;
        this.service = this.isLeaderView
            ? OrganisationService
            : CampaignService;
    }

    clearAll(): void {
        this.id = null;
        this.limits = null;
    }

    resetLimits(): void {
        this.limits = null;
    }

    setID(): Promise<any> {
        if (this.id) {
            return this.$q.resolve(this.id);
        }
        const deferred = this.$q.defer();
        if (this.isLeaderView) {
            this.campaignDataProvider
                .getOrganisations()
                .then((organisation: Organisation) => {
                    this.id = organisation.oid;
                    deferred.resolve(this.id);
                });
        } else {
            const campaign = this.campaignDataProvider.getActiveCampaign();
            this.id = campaign.campaignid;
            deferred.resolve(this.id);
        }
        return deferred.promise;
    }

    get(): Promise<any> {
        if (this.limits) {
            return this.$q.resolve(this.limits);
        } else if (this.limitsPromise) {
            return this.limitsPromise;
        } else {
            if (this.id) {
                return this.getLimits();
            } else {
                const deferred = this.$q.defer();
                this.setID().then(() => {
                    deferred.resolve(this.getLimits());
                });
                return deferred.promise;
            }
        }
    }

    limitReached(licenseType: string, usersCount: any): boolean {
        if (!this.permissionProvider.hasPermission("USERLIMIT")) {
            return false;
        }
        if (usersCount && this.limits) {
            if (licenseType == "standard") {
                return (
                    usersCount.invites + usersCount.users >=
                    this.limits.users + this.limits.extrausers
                );
            } else if (licenseType == "super") {
                return (
                    usersCount.superinvites + usersCount.superusers >=
                    this.limits.superusers + (this.limits.extrasuperusers ?? 0)
                );
            }
        }
        return false;
    }

    licensesUnavailable(
        licenseType: string,
        currentCount: any,
        licensesNeeded: number
    ): boolean {
        if (!this.permissionProvider.hasPermission("USERLIMIT")) {
            return false;
        }
        let usersCount, limitCount;
        if (licenseType == "super") {
            usersCount = currentCount.superinvites + currentCount.superusers;
            limitCount =
                this.limits.superusers + (this.limits.extrasuperusers ?? 0);
        } else {
            usersCount = currentCount.invites + currentCount.users;
            limitCount = this.limits.users + (this.limits.extrausers ?? 0);
        }

        return usersCount + licensesNeeded > limitCount;
    }

    getLimits(): Promise<any> {
        const deferred = this.$q.defer();
        this.service.limits({ id: this.id }).then(
            (response) => {
                const json = response["data"];
                if (this.limits) {
                    this.limits.setData(json.data);
                } else {
                    this.limits = new Limit(json.data);
                }
                deferred.resolve(this.limits);
            },
            () => deferred.reject()
        );
        this.limitsPromise = deferred.promise;
        return this.limitsPromise;
    }
}

angular
    .module("vcDataProvider")
    .service("LimitDataProvider", LimitDataProvider);
